import React from 'react';
import styled, {css} from 'styled-components';

/**
 * @typedef {'top' | 'bottom' | 'left' | 'right'} Direction
 */

/**
 * @typedef {Object} HoverPopoverProps
 * @property {string} message - The message to display inside the popover.
 * @property {Direction} [direction='top'] - The direction where the popover appears.
 * @property {React.ReactNode} children - The element that triggers the popover on hover.
 */

const OFFSET = '0.50rem'; // now in rem for accessibility
const ARROW_SIZE = 6; // still px for fine control
const POPOVER_BG_COLOR = 'rgba(0, 0, 0, 0.75)';
const Z_INDEX_POPOVER = 100;

const Wrapper = styled.div`
    position: relative;
    display: inline-block;
`;

const Popover = styled.div`
    position: absolute;
    padding: 8px 12px;
    background-color: ${POPOVER_BG_COLOR};
    color: #fff;
    border-radius: 4px;
    font-size: 0.875rem; /* Use rem for font-size too */
    white-space: nowrap;
    z-index: ${Z_INDEX_POPOVER};
    visibility: hidden;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease-in-out;

    ${(props) => getPopoverPositionStyles(props.direction)}
    &::after {
        content: '';
        position: absolute;
        ${(props) => getArrowStyles(props.direction)}
    }
`;

const PopoverContainer = styled.div`
    &:hover ${Popover} {
        visibility: visible;
        opacity: 1;
        pointer-events: auto;
    }
`;

/**
 * Calculates the popover's absolute position based on direction.
 * Applies an offset to separate it from the trigger.
 *
 * @param {Direction} direction
 * @returns {import('styled-components').FlattenSimpleInterpolation}
 */
const getPopoverPositionStyles = (direction) => {
    switch (direction) {
        case 'top':
            return css`
                bottom: calc(100% + ${OFFSET});
                left: 50%;
                transform: translateX(-50%);
            `;
        case 'bottom':
            return css`
                top: calc(100% + ${OFFSET});
                left: 50%;
                transform: translateX(-50%);
            `;
        case 'left':
            return css`
                right: calc(100% + ${OFFSET});
                top: 50%;
                transform: translateY(-50%);
            `;
        case 'right':
        default:
            return css`
                left: calc(100% + ${OFFSET});
                top: 50%;
                transform: translateY(-50%);
            `;
    }
};

/**
 * Calculates the arrow's positioning and orientation based on direction.
 *
 * @param {Direction} direction
 * @returns {import('styled-components').FlattenSimpleInterpolation}
 */
const getArrowStyles = (direction) => {
    switch (direction) {
        case 'top':
            return css`
                top: 100%;
                left: 50%;
                transform: translateX(-50%);
                border-width: ${ARROW_SIZE}px;
                border-style: solid;
                border-color: ${POPOVER_BG_COLOR} transparent transparent transparent;
            `;
        case 'bottom':
            return css`
                bottom: 100%;
                left: 50%;
                transform: translateX(-50%);
                border-width: ${ARROW_SIZE}px;
                border-style: solid;
                border-color: transparent transparent ${POPOVER_BG_COLOR} transparent;
            `;
        case 'left':
            return css`
                left: 100%;
                top: 50%;
                transform: translateY(-50%);
                border-width: ${ARROW_SIZE}px;
                border-style: solid;
                border-color: transparent transparent transparent ${POPOVER_BG_COLOR};
            `;
        case 'right':
        default:
            return css`
                right: 100%;
                top: 50%;
                transform: translateY(-50%);
                border-width: ${ARROW_SIZE}px;
                border-style: solid;
                border-color: transparent ${POPOVER_BG_COLOR} transparent transparent;
            `;
    }
};

/**
 * HoverPopoverUI Component: Displays a popover with an arrow in the given direction, properly spaced.
 *
 * @param {HoverPopoverProps} props
 * @returns {JSX.Element}
 */
export const HoverPopoverUI = ({message, direction = 'top', children}) => {
    return (
        <Wrapper>
            <PopoverContainer>
                {children}
                <Popover direction={direction}>{message}</Popover>
            </PopoverContainer>
        </Wrapper>
    );
};
